import React, { useRef, useState, useMemo, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { FilterMatchMode } from 'primereact/api';
import { useNavigate } from 'react-router-dom';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';

const PendingApprovalRequestsTable = ({
    requests,
    loading,
    onPage,
    lazyParams,
    totalCount,
    selectedRequests,
    setSelectedRequests,
    selectAll,
    onSelectAllChange,
    activeFilters,
    onFiltersChange,
    onResetFilters,
    dealerOptions,
    dealerFilterValue,
    setDealerFilterValue,
    setDealerSearchText,
    dealerCodeOptions,
    dealerCodeFilterValue,
    setDealerCodeFilterValue,
    setDealerCodeSearchText,
    warrantyTypeOptions,
    countryOptions,
    warrantyTypeFilterValue,
    setWarrantyTypeFilterValue,
    sortField,
    sortOrder,
    onSortChange
}) => {
    const { th } = useLanguage();
    const navigate = useNavigate();
    const dataTableRef = useRef(null);
    const columnsRef = useRef(null);

    const getColumnsCallback = useCallback((el) => {
        if (el && el.getColumns && !columnsRef.current) {
            columnsRef.current = el.getColumns();
        }
        dataTableRef.current = el;
    }, []);

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    // Generic kolon helper fonksiyonları
    const createTextColumn = (field, headerKey, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px', key = field } = options;

        return (
            <Column
                key={key}
                sortable={sortable}
                field={field}
                header={th(headerKey)}
                style={{ minWidth }}
                body={body}
                filter
                filterField={filterField}
                filterPlaceholder={th('common.search')}
                showFilterMenu={true}
                filterMatchModeOptions={textMatchModeOptions}
                showClearButton={false}
            />
        );
    };

    const createDropdownColumn = (field, headerKey, filterElement, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px', key = field, showFilterMenu = false } = options;

        return <Column key={key} sortable={sortable} field={field} header={th(headerKey)} style={{ minWidth }} body={body} filter filterField={filterField} filterElement={filterElement} showFilterMenu={showFilterMenu} />;
    };

    const handleSort = (e) => {
        onSortChange?.(e);
    };

    const checkboxBodyTemplate = (rowData) => {
        const isChecked = selectedRequests.some((item) => item.id === rowData.id);
        return (
            <Checkbox
                checked={isChecked}
                disabled={selectAll}
                onChange={(e) => {
                    if (e.checked) {
                        setSelectedRequests([...selectedRequests, rowData]);
                    } else {
                        setSelectedRequests(selectedRequests.filter((item) => item.id !== rowData.id));
                    }
                }}
            />
        );
    };

    const headerCheckboxTemplate = () => {
        return (
            <Checkbox
                checked={selectAll}
                onChange={(e) => onSelectAllChange?.(e.checked)}
            />
        );
    };

    const handleEdit = (rowData) => {
        navigate(`/warranty-management/warranty-requests/claims/${rowData.id}`);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex justify-content-center align-items-center">
                <i onClick={() => handleEdit(rowData)} className="pi pi-eye cursor-pointer text-blue-500 hover:text-blue-700" style={{ color: '#0B3187' }} title={th('common.view')}></i>
            </div>
        );
    };

    const actionFilterTemplate = () => {
        return (
            <div className="flex justify-content-center align-items-center">
                <i onClick={onResetFilters} className="pi pi-filter-slash cursor-pointer text-blue-500 hover:text-blue-700" style={{ color: '#0B3187', fontSize: '1rem' }} title={th('filter.reset')}></i>
            </div>
        );
    };

    const claimNoBodyTemplate = (rowData) => {
        return (
            <span onClick={() => handleEdit(rowData)} className="cursor-pointer text-primary hover:underline" style={{ color: '#0B3187', fontWeight: '500' }}>
                {rowData.warrClaimsNo || '-'}
            </span>
        );
    };

    // PrimeReact kolon filtresi (row filter) -> mevcut server-side filtre akışına bağlanır
    const tableFilters = useMemo(
        () => ({
            dealerId: {
                value: activeFilters?.dealerId ?? null,
                matchMode: FilterMatchMode.IN
            },
            bosBranchCode: {
                value: activeFilters?.bosBranchCode ?? null,
                matchMode: FilterMatchMode.IN
            },
            country: {
                value: activeFilters?.country ?? null,
                matchMode: FilterMatchMode.IN
            },
            chassisNo: {
                value: activeFilters?.chassisNo ?? null,
                matchMode: activeFilters?.chassisNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            warrClaimsNo: {
                value: activeFilters?.warrClaimsNo ?? null,
                matchMode: activeFilters?.warrClaimsNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            warrType: {
                value: activeFilters?.warrType ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            totalPrice: {
                value: activeFilters?.totalPrice ?? null,
                matchMode: activeFilters?.totalPriceMatchMode ?? FilterMatchMode.CONTAINS
            },
            laborTotalAmount: {
                value: activeFilters?.laborTotalAmount ?? null,
                matchMode: activeFilters?.laborTotalAmountMatchMode ?? FilterMatchMode.CONTAINS
            },
            otherCostTotalAmount: {
                value: activeFilters?.otherCostTotalAmount ?? null,
                matchMode: activeFilters?.otherCostTotalAmountMatchMode ?? FilterMatchMode.CONTAINS
            }
        }),
        [activeFilters]
    );

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};

        const updatedFilters = {
            ...(activeFilters || {}),
            dealerId: activeFilters?.dealerId ?? null,
            bosBranchCode: activeFilters?.bosBranchCode ?? null,
            bosBranchCodeMatchMode: FilterMatchMode.CONTAINS,
            country: 'country' in filters ? filters.country?.value ?? null : activeFilters?.country ?? null,
            countryMatchMode: FilterMatchMode.EQUALS,
            chassisNo: 'chassisNo' in filters ? filters.chassisNo?.value ?? null : activeFilters?.chassisNo ?? null,
            chassisNoMatchMode: 'chassisNo' in filters ? filters.chassisNo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.chassisNoMatchMode ?? FilterMatchMode.CONTAINS,
            warrClaimsNo: 'warrClaimsNo' in filters ? filters.warrClaimsNo?.value ?? null : activeFilters?.warrClaimsNo ?? null,
            warrClaimsNoMatchMode: 'warrClaimsNo' in filters ? filters.warrClaimsNo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.warrClaimsNoMatchMode ?? FilterMatchMode.CONTAINS,
            warrType: 'warrType' in filters ? filters.warrType?.value ?? null : activeFilters?.warrType ?? null,
            warrTypeMatchMode: 'warrType' in filters ? FilterMatchMode.EQUALS : FilterMatchMode.EQUALS,
            totalPrice: 'totalPrice' in filters ? filters.totalPrice?.value ?? null : activeFilters?.totalPrice ?? null,
            totalPriceMatchMode: 'totalPrice' in filters ? filters.totalPrice?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.totalPriceMatchMode ?? FilterMatchMode.CONTAINS,
            laborTotalAmount: 'laborTotalAmount' in filters ? filters.laborTotalAmount?.value ?? null : activeFilters?.laborTotalAmount ?? null,
            laborTotalAmountMatchMode: 'laborTotalAmount' in filters ? filters.laborTotalAmount?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.laborTotalAmountMatchMode ?? FilterMatchMode.CONTAINS,
            otherCostTotalAmount: 'otherCostTotalAmount' in filters ? filters.otherCostTotalAmount?.value ?? null : activeFilters?.otherCostTotalAmount ?? null,
            otherCostTotalAmountMatchMode: 'otherCostTotalAmount' in filters ? filters.otherCostTotalAmount?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.otherCostTotalAmountMatchMode ?? FilterMatchMode.CONTAINS
        };
        onFiltersChange?.(updatedFilters);
    };

    // Dealer multi-select filter element
    const dealerFilterTemplate = () => {
        return (
            <MultiSelect
                value={activeFilters?.dealerId ?? null}
                options={dealerOptions || []}
                onChange={(e) => {
                    const selected = e.value;
                    onFiltersChange({
                        ...activeFilters,
                        dealerId: Array.isArray(selected) && selected.length > 0 ? selected : null
                    });
                }}
                onHide={() => {
                    setDealerFilterValue('');
                    setDealerSearchText('');
                }}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                resetFilterOnHide
                filterBy="label"
                filterPlaceholder={th('common.search')}
                filterValue={dealerFilterValue}
                onFilter={(e) => {
                    setDealerFilterValue(e.filter);
                    setDealerSearchText(e.filter);
                }}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} seçildi"
                style={{ minWidth: '12rem' }}
            />
        );
    };

    const dealerCodeFilterTemplate = () => {
        return (
            <MultiSelect
                value={activeFilters?.bosBranchCode ?? null}
                options={dealerCodeOptions || []}
                onChange={(e) => {
                    const selected = e.value;
                    onFiltersChange({
                        ...activeFilters,
                        bosBranchCode: Array.isArray(selected) && selected.length > 0 ? selected : null,
                        bosBranchCodeMatchMode: FilterMatchMode.CONTAINS
                    });
                }}
                onHide={() => {
                    setDealerCodeFilterValue('');
                    setDealerCodeSearchText('');
                }}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                resetFilterOnHide
                filterBy="label"
                filterPlaceholder={th('common.search')}
                filterValue={dealerCodeFilterValue}
                onFilter={(e) => {
                    setDealerCodeFilterValue(e.filter);
                    setDealerCodeSearchText(e.filter);
                }}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} seçildi"
                style={{ minWidth: '12rem' }}
            />
        );
    };

    // Warranty Type dropdown filter element
    const warrantyTypeFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={warrantyTypeOptions || []}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                filterBy="label"
                filterPlaceholder={th('common.search')}
                filterValue={warrantyTypeFilterValue}
                onFilter={(e) => {
                    setWarrantyTypeFilterValue(e.filter);
                }}
                style={{ minWidth: '12rem' }}
            />
        );
    };

    const [countryFilterValue, setCountryFilterValue] = useState('');

    const countryFilterTemplate = () => {
        return (
            <MultiSelect
                value={activeFilters?.country ?? null}
                options={countryOptions || []}
                onChange={(e) => {
                    const selected = e.value;
                    onFiltersChange({
                        ...activeFilters,
                        country: Array.isArray(selected) && selected.length > 0 ? selected : null,
                        countryMatchMode: FilterMatchMode.EQUALS
                    });
                }}
                onHide={() => setCountryFilterValue('')}
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                resetFilterOnHide
                filterBy="label"
                filterValue={countryFilterValue}
                onFilter={(e) => setCountryFilterValue(e.filter)}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} seçildi"
                style={{ minWidth: '12rem' }}
            />
        );
    };

    return (
        <StickyScrollTable deps={[requests]}>
        <DataTable
            value={requests}
            totalRecords={totalCount}
            className="p-datatable-gridlines"
            showGridlines
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows || 10}
            onPage={onPage}
            dataKey="id"
            loading={loading}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            emptyMessage={th('warrantyClaims.table.noRecordsFound')}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            tableStyle={{ minWidth: '60rem' }}
            ref={getColumnsCallback}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            sortMode="single"
            pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
        >
            <Column
                bodyClassName="text-center"
                headerClassName="text-center"
                body={checkboxBodyTemplate}
                header={headerCheckboxTemplate}
                exportable={false}
                style={{ width: '3%', minWidth: '50px' }}
                sortable={false}
                filter={true}
                filterElement={() => null}
                showFilterMenu={false}
                showClearButton={false}
            />
            <Column
                key="action"
                body={actionBodyTemplate}
                bodyClassName="text-center"
                headerClassName="text-center"
                header={th('common.actions')}
                exportable={false}
                style={{ width: '5%', minWidth: '60px' }}
                sortable={false}
                filter={true}
                filterElement={actionFilterTemplate}
                showFilterMenu={false}
                showClearButton={false}
            />
            {createDropdownColumn('bosBranchCode', 'customer.dealerCode', dealerCodeFilterTemplate, {
                key: 'dealerCode',
                minWidth: '100px',
                showFilterMenu: false
            })}
            {createDropdownColumn('bosBranchName', 'common.dealer', dealerFilterTemplate, {
                key: 'dealerName',
                filterField: 'dealerId',
                body: (rowData) => createTruncatedTextForTable(rowData.bosBranchName, 40, `dealer-${rowData.id}`),
                minWidth: '180px'
            })}
            {createDropdownColumn('country', 'warrantyClaims.table.country', countryFilterTemplate, {
                minWidth: '130px',
                showFilterMenu: false
            })}
            {createTextColumn('chassisNo', 'warrantyClaims.table.chassisNo', { minWidth: '140px' })}
            {createTextColumn('warrClaimsNo', 'warrantyClaims.table.warrClaimsNo', {
                body: claimNoBodyTemplate,
                minWidth: '110px'
            })}
            {createDropdownColumn('warrType', 'warrantyClaims.table.warrantyType', warrantyTypeFilterTemplate, {
                minWidth: '120px'
            })}
            {createTextColumn('totalPrice', 'warrantyClaims.table.totalPrice', { minWidth: '120px' })}
            {createTextColumn('laborTotalAmount', 'warrantyClaims.table.laborPrice', { minWidth: '120px' })}
            {createTextColumn('otherCostTotalAmount', 'warrantyClaims.table.externalWorkPart', { minWidth: '150px' })}
        </DataTable>
        </StickyScrollTable>
    );
};

export default PendingApprovalRequestsTable;
